import type { State } from '../configureStore'
import { ExternalLists as ActionTypes } from '../actionTypes'
import * as AuthActions from './auth.actions'
import {
  defaultState,
  TotalsPublicObjects,
  PublicObject,
  ShadowAccountItem,
  ShadowNetworkItem,
  ShadowAccess
} from '../reducers/external.lists/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import { ResourceTypeEnum } from '@src/endpoints/services/commonHdr.service'
import type { AccountInfo } from '@src/endpoints/services/base.service'
import {
  TenantMgr,
  SetShadowAccountNameReq
} from '@src/endpoints/services/tenantMgr.service'
import {
  ShadowType,
  ShadowNetwork,
  ShadowAccountDetail,
  GraphQueryMgr
} from '@src/endpoints/services/graphQueryMgr.service'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import { getAccountNameById } from '@src/utils/accounts'
import uuidv4 from 'uuid/v4'

import { forEach } from 'lodash'
import { getActionsFormat } from '@src/utils/resource.utils'

// Action Creators
export type ActionTypes =
  | getStatSuccessAction
  | changeStateAction
  | getPublicObjectsAction
  | cleanerStatAction
  | requestPendingAction
  | cleaningAction
  | serverErrorsAction

export const getStatSuccess = ReduxHelpers.Common.Actions.setToState(
  ActionTypes.GET_STAT_SUCCESS,
  defaultState
)
type getStatSuccessAction = ReturnType<typeof getStatSuccess>

export const changeState = ReduxHelpers.Common.Actions.changeState(
  ActionTypes.CHANGE_STATE,
  defaultState
)

type changeStateAction = ReturnType<typeof changeState>

export const cleanerStat = ReduxHelpers.Common.Actions.cleanerState(
  ActionTypes.CLEANER_STAT,
  defaultState
)

type cleanerStatAction = ReturnType<typeof cleanerStat>

export const serverErrors = (errors: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SERVER_ERRORS, {
    errors
  })
type serverErrorsAction = ReturnType<typeof serverErrors>

export const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export const getPublicObjects = (
  totalsPublicObjects: TotalsPublicObjects,
  publicObjectsList: PublicObject[]
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_PUBLIC_OBJECTS, {
    totalsPublicObjects,
    publicObjectsList,
    isPending: false
  })
type getPublicObjectsAction = ReturnType<typeof getPublicObjects>

export const cleaning = () =>
  ReduxHelpers.Actions.createAction(ActionTypes.CLEANING)
type cleaningAction = ReturnType<typeof cleaning>

/* Public Objects */
export const parseActions = (obj: PublicObject) =>
  getActionsFormat(obj, {
    readKey: 'read_action_count',
    writeKey: 'write_action_count',
    adminKey: 'admin_action_count'
  })

export const parsePublicObject = (
  item: PublicObject,
  account_info: AccountInfo[]
) => ({
  ...item,
  account_name: getAccountNameById(item.account_id, account_info),
  actions: parseActions(item)
})
export function getPublicObjectsData(
  type?: ResourceTypeEnum,
  callbackFn?: () => void
) {
  return (dispatch: any, getState: () => State) => {
    const { filter, auth } = getState()
    const { tenant } = auth
    const { account_info = [] } = tenant || {}
    dispatch(requestPending(true))
    GraphQueryMgr.PublicObjectQuery({
      params: paramsWithFilter({ type }, filter)
    })
      .then(res =>
        defaultHandleStatus(
          res,
          err => {
            dispatch(requestPending(false))
            if (callbackFn) {
              callbackFn()
            }
          },
          true
        )
      )
      .then(res => {
        const { public_objects_info = [], entity_info = [] } = res
        const totals = {} as TotalsPublicObjects
        forEach(public_objects_info, counter => {
          totals[ResourceTypeEnum[counter.source_type]] =
            counter.public_count || 0
        })
        const list = entity_info.map(item =>
          parsePublicObject(item, account_info)
        )
        dispatch(getPublicObjects(totals, list))
        if (callbackFn) {
          callbackFn()
        }
      })
      .catch((res: Response) => {
        dispatch(requestPending(false))
        if (callbackFn) {
          callbackFn()
        }
      })
  }
}

/* Shadow Access */
interface SetShadowAccountNameOptions {
  accountId: string
  accountName: string
  type: string
  tab: ShadowType
}

export function setShadowAccountName({
  accountId,
  accountName,
  type,
  tab
}: SetShadowAccountNameOptions) {
  return (dispatch: any, getState: () => State) => {
    const { auth } = getState()
    const { tenant } = auth
    const { shadow_accounts = {} } = tenant
    shadow_accounts[accountId] = accountName
    dispatch(requestPending(true))
    const params: SetShadowAccountNameReq = {
      req_hdr: {
        tid: uuidv4()
      },
      account_id: accountId,
      account_name: accountName
    }
    TenantMgr.SetShadowAccountName({ params, options: { signal: null } })
      .then(res =>
        defaultHandleStatus(
          res,
          err => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        dispatch(getShadowAccessList({ type, tab }))
        dispatch(AuthActions.getTenantSuccess({ ...tenant, shadow_accounts }))
        dispatch(requestPending(false))
      })
      .catch((res: Response) => {
        dispatch(requestPending(false))
      })
  }
}

export const parseShadowAccountDetail = (
  data: ShadowAccountDetail,
  account_info: AccountInfo[]
): ShadowAccountItem => ({
  ...data,
  account_name: getAccountNameById(data.account_id, account_info)
})

export const getShadowNetworkIpAndName = (item: ShadowNetwork) => {
  const { name } = item || ({} as ShadowNetwork)
  if ((name || '').includes('(')) {
    const ipAndName = (name || '').split('(')
    return {
      network_ip: ipAndName[0],
      network_name: (ipAndName[1] || '').slice(0, -2)
    }
  }
  return {
    network_ip: name,
    network_name: name
  }
}

export const parseShadowNetworkDetail = (
  data: ShadowNetwork,
  account_info: AccountInfo[]
): ShadowNetworkItem => ({
  ...data,
  ...getShadowNetworkIpAndName(data),
  account_name: getAccountNameById(data.account_id, account_info)
})

export function getShadowAccessList({
  type,
  tab = ShadowType.SHADOW_UNKNOWN_ACCOUNT
}: {
  type?: string
  tab?: ShadowType
}) {
  return (dispatch: any, getState: () => State) => {
    const { filter, auth } = getState()
    const { tenant } = auth
    const { account_info = [] } = tenant || {}
    dispatch(requestPending(true))
    GraphQueryMgr.ShadowAccessQuery({
      params: paramsWithFilter(
        {
          type: tab
        },
        filter
      )
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        const parsedData: ShadowAccess = {
          unknown_account: (res.unknown_account || []).map(x =>
            parseShadowAccountDetail(x, account_info)
          ),
          known_account: (res.known_account || []).map(x =>
            parseShadowAccountDetail(x, account_info)
          ),
          shadow_network: (res.shadow_network || []).map(x =>
            parseShadowNetworkDetail(x, account_info)
          )
        }
        dispatch(getStatSuccess<ShadowAccess>('shadowAccessLists', parsedData))
      })
      .catch((res: Response & { name?: string }) => {
        const isAbort = res?.name === 'AbortError'
        if (!isAbort) {
          dispatch(requestPending(false))
        }
      })
  }
}
